import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import { FastlyImage } from '@components/common/image'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { GIT } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['wd'].trialCourseQuery}`

export const GITSeo = formatSeoConfig({
  title: 'Git for Kids: Learn Version Control Online',
  description:
    'Git version control for kids, taught by expert instructors. Track code changes and collaborate like a real developer at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/git-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-light-v1.jpg',
})

const BegineerGuide = () => (
  <div className="space-y-3">
    <h3 className="font-600 text-lg lg:text-xl">Why Should Kids Learn GIT?</h3>
    <p>Learning GIT is a great way for kids to:</p>
    <ul>
      <li>
        Boost creativity by confidently building and managing their own coding
        projects
      </li>
      <li>
        Learn problem-solving skills by fixing mistakes using version history
      </li>
      <li>
        Understand collaboration, an essential part of real-world software
        development
      </li>
      <li>
        Express themselves by working on websites, games, and apps without
        losing progress
      </li>
      <li>
        Gain a strong foundation for learning advanced coding tools and teamwork
        platforms like GITHub
      </li>
    </ul>
    <p>
      GIT helps kids move from coding alone to thinking and working like real
      developers.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      How Do You Explain GIT to a Child?
    </h3>
    <p>
      Imagine you are writing a story in a notebook. Every time you make
      changes, you take a photocopy of that page and keep it safe. If you make a
      mistake later, you can go back to an older copy without losing your work.
    </p>
    <p>
      That is what GIT does for coding, it is like a magic notebook that saves
      every version of your project. Kids can see what changed, who changed it,
      and even work together on the same project without mixing things up.
    </p>
    <p>
      Explaining it this way makes it easy for kids to understand GIT as a “time
      machine for code.”
    </p>
    <h3 className="font-600 text-lg lg:text-xl">What Does GIT Stand For?</h3>
    <div>
      <FastlyImage
        src={`/images/courses/git/git-1.jpg`}
        alt="GIT Definition"
        width={512}
        height={341}
      />
    </div>
    <p>
      GIT stands for Global Information Tracker. It is a system used by
      programmers to save and manage versions of their code. You can think of it
      as a time machine for projects, it remembers every change, so you can go
      back if something goes wrong. This is fundamental for anyone learning GIT
      for kids, because it teaches them how to track progress, collaborate, and
      stay organized while coding.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      An Easy Way to Understand GIT for kids
    </h3>
    <p>
      Here’s a simple analogy to help you understand GIT. Imagine you are
      writing a superhero comic book. Every time you finish a page, you make a
      copy and keep it safe. If you make a mistake later or want to change the
      story, you can always go back to the earlier version without losing your
      work. That safe copy is your GIT version.
    </p>
    <p>
      GIT is like a time machine for your projects, it keeps track of every
      change you make. It helps kids stay organized, fix mistakes easily, and
      even work together on the same project without mixing things up.
    </p>
    <p>
      This is exactly how kids learn GIT in our kids GIT classes; turning their
      coding projects into stories they can save, improve, and share!{' '}
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      What Can Kids Learn with GIT?
    </h3>
    <p>Kids can learn a lot with GIT, including:</p>

    <ul>
      <li>
        <strong>Version control:</strong> How to save and track every change in
        their code
      </li>
      <li>
        <strong>Problem-solving:</strong> Fixing mistakes by going back to
        earlier versions
      </li>
      <li>
        <strong>Organization skills:</strong> Keeping projects neat and
        structured
      </li>
      <li>
        <strong>Collaboration:</strong> Working on the same project with friends
        without overwriting each other’s work
      </li>
      <li>
        <strong>Real-world coding habits:</strong> Using tools that professional
        developers rely on every day
      </li>
    </ul>
    <p>
      With GIT, kids don’t just code—they learn how to think, create, and
      collaborate like real programmers.
    </p>
    <div>
      <FastlyImage
        src={`/images/courses/git/git-2.jpg`}
        alt="What Can Kids Learn with GIT?"
        width={512}
        height={341}
      />
    </div>
  </div>
)

const GITPage = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...GITSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[GIT].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Learn GIT with Top AI & Coding Course Instructors for Kids and Teens"
        description="Empower your child to move from just using technology to mastering it. Codingal’s web development course introduces GIT to kids and teens, one of the most important tools developers use worldwide. By learning GIT, children understand how to save versions of their code, collaborate on projects, and track changes just like professional programmers. With expert instructors, kids will not only design and structure their own websites but also learn the discipline of version control, a skill that builds confidence and teamwork. This hands-on journey blends creativity with core coding practices, giving students a strong foundation to grow into advanced fields like AI, software development, and innovation."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={GIT}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={GIT} />
          <CourseFeatures
            courseTitle={GIT}
            title={<Trans>Engaging Website Development classes for kids</Trans>}
          />
          <ClassCurriculum
            courseName={GIT}
            title="Our GIT Class Curriculum"
            description={
              <>
                The GIT curriculum for kids is designed to take students from
                foundational concepts to practical coding collaboration skills.
                Beginning with setting up GIT and learning basic commands,
                students gradually explore how to save versions of projects,
                undo mistakes, and collaborate on shared code. As they advance,
                kids learn branching, merging, and using GITHub to publish and
                share projects. Each lesson builds on the previous one, enabling
                students to manage coding projects confidently, work in teams,
                and apply version control in hands-on, real-world projects.
                It&apos;s like giving them a time machine for coding LEGOs!
              </>
            }
          />
          <Activities courseName={GIT} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn GIT from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach. They’re truly
                  amazing, and it shows in our exceptional Net Promoter Score
                  (NPS) of 86! Students and parents consistently rate our
                  teachers 4.9 out of 5, and our curriculum earns a fantastic
                  4.8 out of 5. We’re so proud to have a faculty that’s 91%
                  female and 9% male, creating an inclusive, encouraging
                  learning environment where everyone feels welcome. Each tutor
                  is trained to understand individual student needs and deliver
                  personalized support, even in group settings. Plus, students
                  have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help. This makes learning GIT a
                  truly supported experience, especially through our virtual GIT
                  lessons that connect kids with top educators worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Website Development
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Codingal offers a nurturing environment where young learners
                  can explore the world of coding without fear. Our structured
                  curriculum, combined with supportive instructors, ensures that
                  students build a strong foundation in GIT, preparing them for
                  exciting future endeavors in AI & coding.
                </span>
              </Trans>
            }
            courseName={GIT}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters GIT through a STEM.org -
                  accredited program built on proven educational frameworks like
                  Bloom’s Taxonomy, BIDE, and STEAM. Our curriculum is aligned
                  with Common Core, state standards, AP Computer Science goals,
                  and the K - 12 Computer Science Framework - ensuring learning
                  that’s both age-appropriate and college-ready. Through
                  hands-on, project-based learning, students create real-world
                  apps like temperature converters and scientific calculators,
                  applying math, science, and engineering logic to solve
                  everyday problems. Backed by STEM.org’s rigorous audits, every
                  GIT lesson lays a strong, future-proof foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your GIT certifications"
            description={
              <>
                In the Website Development track, students in grades 6 - 12
                begin their journey with GIT, the foundational language of the
                web. They learn how to structure web pages using elements like
                headings, paragraphs, links, images, lists, tables, and forms.
                As they progress, students apply semantic GIT to improve
                accessibility and build pages that mirror real-world websites.
                Through hands-on lessons and creative projects, they transform
                code into fully functional web pages. By the end of the GIT
                module, students not only understand how websites are built but
                also develop a strong foundation for diving into web design,
                interactivity with JavaScript, and more advanced web
                technologies.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our GIT Classes Are Appropriate for Middle School & High School Students"
            description={
              <>
                At Codingal, our foundational GIT curriculum introduces middle
                and high school students to the essential tool that developers
                use worldwide: version control. Starting with simple concepts
                like repositories, commits, and saving changes, students engage
                in beginner-friendly activities that show them how to organize
                and track their coding projects.
                <br />
                As they progress, they explore more advanced concepts such as
                branches, merges, and collaboration on GITHub—ensuring a clear,
                steady learning curve. With each project, students learn how to
                work like real developers, save every version of their code, and
                confidently collaborate with others without losing progress.
                <br />
                By the end of the course, students not only understand GIT as a
                version control system but also gain the confidence and skills
                to manage coding projects, work in teams, and step into advanced
                areas of AI & software development.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />
          <ContentCard
            urlQuery={urlQuery}
            title={<>The Ultimate Guide to GIT for Kids</>}
            description={<BegineerGuide />}
          />
          <Faq
            courseName={GIT}
            title="Frequently Asked Questions: GIT for Kids"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={GIT}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Git for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default GITPage
